import {
  Plan,
  LowIncomeSubsidyStatus,
  SearchResultPlan,
  PlanCategoryType,
} from "../@types";
import { $$ } from "../helpers";
import { CONSTANTS } from "./CONSTANTS";

const {
  LIS_LEVEL_1A,
  LIS_LEVEL_1A_DN,
  LIS_LEVEL_1B,
  LIS_LEVEL_1C,
  LIS_LEVEL_2,
  LIS_LEVEL_3,
  LIS_LEVEL_4_100,
  LIS_LEVEL_4_25,
  LIS_LEVEL_4_50,
  LIS_LEVEL_4_75,
} = LowIncomeSubsidyStatus;

export const getPartDPremiumOrLIS = ({
  lisToUse,
  plan,
}: {
  lisToUse: LowIncomeSubsidyStatus;
  plan: Plan | SearchResultPlan;
}): number => {
  if (!plan.lis) {
    return plan.partd_premium;
  }

  if (
    [
      LIS_LEVEL_1A,
      LIS_LEVEL_1A_DN,
      LIS_LEVEL_1B,
      LIS_LEVEL_1C,
      LIS_LEVEL_2,
      LIS_LEVEL_3,
      LIS_LEVEL_4_100,
    ].includes(lisToUse)
  ) {
    return plan.lis.level_100;
  } else if ([LIS_LEVEL_4_75].includes(lisToUse)) {
    return plan.lis.level_75;
  } else if ([LIS_LEVEL_4_50].includes(lisToUse)) {
    return plan.lis.level_50;
  } else if ([LIS_LEVEL_4_25].includes(lisToUse)) {
    return plan.lis.level_25;
  } else {
    return plan.partd_premium;
  }
};

export const getDeductibleToUse = ({
  drugPlanDeductible,
  contractYear,
}: {
  drugPlanDeductible: number;
  contractYear: string;
}): number => {
  const maxDrugDeductible: number =
    CONSTANTS.maxLisDrugDeductibles[contractYear] || drugPlanDeductible;

  return drugPlanDeductible < maxDrugDeductible
    ? drugPlanDeductible
    : maxDrugDeductible;
};

export const getAnnualDrugDeductible = ({
  lisToUse,
  drugPlanDeductible,
  contractYear,
}: {
  lisToUse: LowIncomeSubsidyStatus;
  drugPlanDeductible: number;
  contractYear: string;
}): number => {
  if (lisToUse === LowIncomeSubsidyStatus.LIS_NO_HELP) {
    return drugPlanDeductible;
  } else if (/LEVEL_[123]/.test(lisToUse)) {
    // LIS levels 1-3 always have a $0 deductible (will apply to all LIS levels in 2024)
    return 0;
  } else {
    return getDeductibleToUse({
      drugPlanDeductible,
      contractYear,
    });
  }
};

export const getDisplayAnnualDrugDeductible = ({
  lisToUse,
  drugPlanDeductible,
  contractYear,
}: {
  lisToUse: LowIncomeSubsidyStatus;
  drugPlanDeductible: number;
  contractYear: string;
}): string => {
  return $$(
    getAnnualDrugDeductible({
      lisToUse,
      drugPlanDeductible,
      contractYear,
    })
  );
};

export const getPriceForYear = (
  prices: { [key: string]: number },
  year?: string
): number => (year && prices[year]) || prices[Object.keys(prices).pop() || ""];

export const getPartBPremiumForYear = (year?: string): number =>
  getPriceForYear(CONSTANTS.partBPremium, year);

export const getPartBPremium = (plan: Plan | SearchResultPlan): number => {
  const premium = getPartBPremiumForYear(plan.contract_year);

  return plan.category === PlanCategoryType.MEDICARE_MEDICAID_PLAN
    ? 0
    : premium;
};

export const getPartBHealthDeductibleForYear = (year?: string): number =>
  getPriceForYear(CONSTANTS.partBHealthDeductible, year);

export const showMMPBenefits = (plan: Plan): boolean | null => {
  return (
    plan.additional_supplemental_benefits &&
    !!plan.additional_supplemental_benefits.mmp_benefits.length &&
    plan.category === PlanCategoryType.MEDICARE_MEDICAID_PLAN
  );
};
